import { LegalLayout, LegalSection } from "@/components/LegalLayout";
import { Seo } from "@/components/Seo";

export default function Privacy() {
  return (
    <LegalLayout title="Privacy Policy" updated="July 2026">
      <Seo title="Privacy Policy | Aliwer" description="How Aliwer collects, uses, and protects your information, including cookies, GDPR/CCPA rights, sub-processors, and data processing." path="/privacy" />
      <p className="text-sm text-fog leading-relaxed">
        This Privacy Policy explains how Aliwer LLC ("Aliwer", "we", "us") — a Texas
        limited liability company founded, owned, and operated by Lorenzo Openito — collects,
        uses, and protects information when you use our website and services. By using the
        site, you agree to the practices described here.
      </p>

      <LegalSection n="01" title="Information we collect">
        <p>We collect information you provide directly, including:</p>
        <ul className="list-disc pl-5 space-y-1">
          <li>Account details — name, email address, company, and role (client or partner).</li>
          <li>Consultation bookings — selected service, date, time, and any notes.</li>
          <li>Partner applications — expertise, experience, links, and messages.</li>
          <li>Contact submissions — name, email, company, phone, and message content.</li>
        </ul>
        <p>
          We also collect limited technical data (such as log data) needed to operate
          and secure the service.
        </p>
      </LegalSection>

      <LegalSection n="02" title="How we use information">
        <ul className="list-disc pl-5 space-y-1">
          <li>To create and manage your account and authenticate your sessions.</li>
          <li>To schedule and confirm consultations and process partner applications.</li>
          <li>To send transactional emails (confirmations, status updates, notifications).</li>
          <li>To respond to inquiries and improve our services.</li>
        </ul>
      </LegalSection>

      <LegalSection n="03" title="Email communications">
        <p>
          We send transactional emails related to your bookings and applications. Any
          marketing emails will include an unsubscribe option and our postal address, in
          accordance with the CAN-SPAM Act.
        </p>
      </LegalSection>

      <LegalSection n="04" title="Data storage & security">
        <p>
          Account passwords are hashed with bcrypt (never stored in plaintext or a
          reversible form), and sessions use signed, short-lived tokens. As a cybersecurity
          consulting firm, we apply industry practices to safeguard data. No method of
          transmission or storage is completely secure, and we cannot guarantee absolute
          security.
        </p>
      </LegalSection>

      <LegalSection n="05" title="Cookies & analytics">
        <p>
          We use essential cookies required for the site to function (such as
          authentication) and, with your consent, optional analytics cookies that help us
          understand and improve usage. You can accept or decline optional cookies via the
          banner shown on your first visit, and change your browser settings at any time.
        </p>
      </LegalSection>

      <LegalSection n="06" title="Legal bases (GDPR)">
        <p>
          Where the EU/UK GDPR applies, we process personal data under one or more of the
          following legal bases: performance of a contract (to provide the services you
          request), our legitimate interests (to operate, secure, and improve the service),
          your consent (for optional cookies and marketing), and compliance with legal
          obligations.
        </p>
      </LegalSection>

      <LegalSection n="07" title="Sub-processors & service providers">
        <p>
          We use trusted third-party providers to operate the service: <span className="text-paper">Emergent</span> (cloud
          hosting &amp; database), <span className="text-paper">Resend</span> (transactional email delivery), and
          <span className="text-paper"> Anthropic</span> (the Claude model that powers AI features in our BArecord and
          SecureDesk products). When you submit content for AI analysis, it is sent to Anthropic solely to generate your
          result; it is <span className="text-paper">not used to train any AI model</span>. These providers process data
          only on our instructions and under appropriate contractual safeguards. A current list of sub-processors is
          available on request at legal@aliwers.com.
        </p>
      </LegalSection>

      <LegalSection n="08" title="International data transfers">
        <p>
          We are based in the United States and may process data in the U.S. and other
          countries. Where required, we rely on appropriate transfer mechanisms (such as
          Standard Contractual Clauses) to protect personal data transferred across borders.
        </p>
      </LegalSection>

      <LegalSection n="09" title="Your rights & choices">
        <p>
          Depending on your location (for example under the Texas Data Privacy and Security
          Act, CCPA/CPRA, and the EU/UK GDPR), you may have the right to access, correct,
          delete, or port your personal information; to opt out of the sale or sharing of
          personal information and targeted advertising; to withdraw consent; and to appeal a
          decision. Aliwer does not sell your personal information. To exercise any right,
          contact us using the details below; we will respond within the timeframes required
          by applicable law and will not discriminate against you for exercising your rights.
        </p>
      </LegalSection>

      <LegalSection n="10" title="Data Processing Addendum (DPA)">
        <p>
          Business customers who require a Data Processing Addendum for the BArecord or
          SecureDesk products may request one by contacting us. The DPA governs our role as a
          processor of customer-provided data and includes GDPR-required terms.
        </p>
      </LegalSection>

      <LegalSection n="11" title="Data retention">
        <p>
          We retain personal information for as long as your account is active or as
          needed to provide services, comply with legal obligations, resolve disputes,
          and enforce agreements. You may request deletion of your account data at any
          time.
        </p>
      </LegalSection>

      <LegalSection n="12" title="Children's privacy">
        <p>Our services are intended for businesses and professionals and are not directed to children under 13.</p>
      </LegalSection>

      <LegalSection n="13" title="Changes to this policy">
        <p>We may update this policy from time to time. Material changes will be reflected by the "Last updated" date above.</p>
      </LegalSection>

      <LegalSection n="14" title="Contact us">
        <p>
          Aliwer LLC<br />
          Registrant / Owner: Lorenzo Openito (Founder &amp; CEO)<br />
          300 State Street, Unit 92532, Southlake, TX 76092<br />
          Email: aliwer.usa@gmail.com<br />
          Phone: 440-463-9002 · 817-993-9336
        </p>
      </LegalSection>
    </LegalLayout>
  );
}
